import {useEffect, useState} from 'react';
import {Suggestion} from './types/Suggestion';
import {Address} from './types/address';
import {getLongFieldValue, getShortFieldValue} from './helpers';

// Add type declarations for google namespace
declare const google: any;
declare const window: any;

let autocompleteService: any = null;
let placesService: any = null;

// Wait for Google Maps to be ready (loaded from index.html)
const waitForGoogleMaps = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }

    // Poll for google.maps.places to become available (max 10 seconds)
    let attempts = 0;
    const maxAttempts = 100;

    const checkLoaded = () => {
      attempts++;
      if (window.google?.maps?.places) {
        resolve();
      } else if (attempts >= maxAttempts) {
        reject(new Error('Timeout waiting for Google Maps to load'));
      } else {
        setTimeout(checkLoaded, 100);
      }
    };

    checkLoaded();
  });
};

// Initialize services
const initializeServices = async () => {
  if (autocompleteService && placesService) {
    return; // Already initialized
  }

  // Wait for Google Maps to be loaded from index.html
  await waitForGoogleMaps();

  if (!autocompleteService) {
    autocompleteService = new google.maps.places.AutocompleteService();
  }

  if (!placesService) {
    // PlacesService requires a div element
    const div = window.document.createElement('div');
    placesService = new google.maps.places.PlacesService(div);
  }
};

const fetchSuggestions = async (
  search: string,
): Promise<[Suggestion[], string | undefined]> => {
  try {
    await initializeServices();

    return new Promise(resolve => {
      autocompleteService!.getPlacePredictions(
        {
          input: search,
          types: ['address'],
        },
        (predictions: any, status: any) => {
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            const suggestions = predictions.map((prediction: any) => {
              return {
                id: prediction.place_id,
                fullAddress: prediction.description,
              };
            });
            resolve([suggestions, undefined]);
          } else if (
            status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            resolve([[], 'Invalid address']);
          } else {
            resolve([[], undefined]);
          }
        },
      );
    });
  } catch (error) {
    console.error('Error fetching suggestions:', error);
    return [[], undefined];
  }
};

export const fetchAddress = async (
  placeId: string,
): Promise<Address | undefined> => {
  try {
    await initializeServices();

    return new Promise(resolve => {
      placesService!.getDetails(
        {
          placeId,
          fields: ['address_components', 'geometry'],
        },
        (place: any, status: any) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const address_components = place.address_components || [];
            const location = place.geometry?.location;

            const streetLine = getLongFieldValue(address_components, [
              'street_number',
              'route',
            ]);

            const secondLine = getLongFieldValue(address_components, [
              'subpremise',
            ]);

            const city = getLongFieldValue(address_components, ['locality']);

            const state = getShortFieldValue(address_components, [
              'administrative_area_level_1',
            ]);

            const country = getShortFieldValue(address_components, ['country']);

            const postalCode = getShortFieldValue(address_components, [
              'postal_code',
            ]);

            resolve({
              location: {
                latitude: location?.lat() || 0,
                longitude: location?.lng() || 0,
              },
              streetLine,
              secondLine,
              state,
              city,
              country,
              postalCode,
            });
          } else {
            resolve(undefined);
          }
        },
      );
    });
  } catch (error) {
    console.error('Error fetching address:', error);
    return undefined;
  }
};

const useAutoCompleteSuggestions = (
  debouncedSearchTerm: string,
): [Suggestion[], string | undefined] => {
  const [suggestions, setSuggestions] = useState<
    [Suggestion[], string | undefined]
  >([[], undefined]);

  useEffect(() => {
    const fetchData = async () => {
      if (debouncedSearchTerm) {
        const nextSuggestions = await fetchSuggestions(debouncedSearchTerm);

        setSuggestions(nextSuggestions);
      } else {
        setSuggestions([[], undefined]);
      }
    };

    fetchData();
  }, [debouncedSearchTerm]);

  return suggestions;
};

export const useAutoComplete = () => {
  return {
    getSuggestions: useAutoCompleteSuggestions,
  };
};

export default useAutoComplete;
